import { VENUES, type VenueId } from "@/config/contracts";
import { AjnaIcon } from "./ajna-icon";
import { MorphoIcon } from "./morpho-icon";

type Props = {
  readonly value: VenueId;
  readonly onChange: (v: VenueId) => void;
};

const ORDER: readonly VenueId[] = ["morpho", "ajna"];

const LEGEND: ReadonlyArray<{ id: VenueId; icon: React.ReactNode; label: string }> = [
  { id: "morpho", icon: <MorphoIcon size={13} />, label: "Morpho" },
  { id: "ajna",   icon: <AjnaIcon   size={13} />, label: "Ajna" },
];

const HelpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <path d="M12 17h.01"/>
  </svg>
);

export const VenueIconToggle = ({ value, onChange }: Props) => (
  <div className="flex flex-col items-end gap-1.5">

    {/* Label row: "Market" + ? tooltip trigger, both baseline-aligned */}
    <div className="hidden md:flex items-center gap-1.5">
      <span
        className="font-mono text-[10px] tracking-[0.18em] uppercase select-none leading-none"
        style={{ color: "var(--ink-4)" }}
      >
        Market
      </span>

      {/* ? trigger */}
      <div className="relative group flex items-center">
        <span
          className="flex items-center cursor-default"
          style={{ color: "var(--ink-5)" }}
          aria-label="Market legend"
        >
          <HelpIcon />
        </span>

        {/* Tooltip */}
        <div
          className="pointer-events-none absolute right-0 bottom-full mb-2 z-50
                     opacity-0 group-hover:opacity-100
                     translate-y-1 group-hover:translate-y-0
                     transition-all duration-150"
          role="tooltip"
        >
          <div
            className="rounded-[var(--radius-md)] px-3 py-2.5 space-y-2"
            style={{
              background: "#252525",
              border: "1px solid var(--line-2)",
              minWidth: "8rem",
            }}
          >
            {LEGEND.map(({ id, icon, label }) => (
              <div key={id} className="flex items-center gap-2.5">
                <span style={{ color: "var(--ink-2)" }}>{icon}</span>
                <span
                  className="font-mono text-[10px] tracking-[0.08em] whitespace-nowrap"
                  style={{ color: "var(--ink-2)" }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Toggle buttons */}
    <div
      role="radiogroup"
      aria-label="Lending venue"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-[var(--radius-sm)]"
      style={{ border: "1px solid var(--line-2)" }}
    >
      {ORDER.map((id) => {
        const active = value === id;
        return (
          <button
            key={id}
            role="radio"
            aria-checked={active}
            aria-label={VENUES[id].shortLabel}
            type="button"
            onClick={() => onChange(id)}
            className="w-10 h-8 flex items-center justify-center rounded-[2px] transition-colors"
            style={{
              background: active ? "rgba(0,0,0,0.48)" : "transparent",
              color: active ? "var(--ink-1)" : "var(--ink-5)",
            }}
          >
            {id === "morpho" ? <MorphoIcon size={18} /> : <AjnaIcon size={19} />}
          </button>
        );
      })}
    </div>
  </div>
);
